import React, { useCallback } from "react";
import {
  useActiveTab,
  useSubFormApproval,
  useProductDetailsApproval,
  useManufacturerSiteApproval
} from "../../../../../hooks";
import CommitteeReviewButtons from "../../CommitteeReviewButtons";
import { FormSectionsLayout } from "./SharedComponents";
import axiosInstance from "../../../../../axiosInstance";

export const FormSectionsCustomer = ({
  subForms,
  requestProgressId,
  prevComments,
  requestDetails,
  filledForm,
  requestId,
  handleAddComment,
  requestProgress,
  children,
  handleAction,
  isReadOnly,
  // Additional props for advanced use cases
  editable = false,
  onFormDataChange = null,
  syncFormSectionsData = null,
  skipApprovalFetch = false,
  errors = null,
  activeTab: externalActiveTab = null,
  onTabChange: externalOnTabChange = null,
  showRequestBadges = true,
  enableAddComment: enableAddCommentProp = true,
  formName = null,
  hasProductDetails = false,
  hasManufacturersSite = false,
  isSendbackMode = false,
  productDetailsData = null,
}) => {
  // State to hold product details and manufacturer site comments
  const [productDetailsComments, setProductDetailsComments] = React.useState([]);
  const [manufacturerSiteComments, setManufacturerSiteComments] = React.useState([]);
  const [commentsRefetchKey, setCommentsRefetchKey] = React.useState(0);

  // fetch product details approval status
  const {
    isApproved: isProductDetailsApproved,
  } = useProductDetailsApproval({
    productDetailsId: productDetailsData?.id,
    requestId: requestId,
    persona: "customer"
  });

  // fetch manufacturer site approval status
  const {
    isApproved: isManufacturerSiteApproved,
  } = useManufacturerSiteApproval({
    manufacturerSiteId: requestDetails?.manufacturersSite?.id,
    requestId: requestId,
    persona: "customer"
  });

  // Fetch product details comments
  React.useEffect(() => {
    const fetchProductDetailsComments = async () => {
      if (!productDetailsData?.id) {
        setProductDetailsComments([]);
        return;
      }
      try {
        const response = await axiosInstance.get(`/api/product-details-comments/enduser/${productDetailsData.id}`);
        setProductDetailsComments(response.data || []);
      } catch (error) {
        console.error('Error fetching product details comments:', error);
        setProductDetailsComments([]);
      }
    };

    fetchProductDetailsComments();
  }, [productDetailsData?.id, commentsRefetchKey]);

  // Fetch manufacturer site comments
  React.useEffect(() => {
    const fetchManufacturerSiteComments = async () => {
      if (!requestDetails?.manufacturersSite?.id) {
        setManufacturerSiteComments([]);
        return;
      }
      try {
        const response = await axiosInstance.get(`/api/manufacturers-site-comments/enduser/${requestDetails.manufacturersSite.id}`);
        setManufacturerSiteComments(response.data || []);
      } catch (error) {
        console.error('Error fetching manufacturer site comments:', error);
        setManufacturerSiteComments([]);
      }
    };

    fetchManufacturerSiteComments();
  }, [requestDetails?.manufacturersSite?.id, commentsRefetchKey]);

  // callback to refetch comments after adding a new one
  const handleAddCommentWrapper = useCallback(async (...args) => {
    if (handleAddComment) {
      await handleAddComment(...args);
    }
    // trigger refetch of comments to update icon
    setCommentsRefetchKey(prev => prev + 1);
  }, [handleAddComment]);

  // callback for custom forms (ProductDetails, ManufacturersSite) to trigger icon update
  const handleCommentAdded = useCallback(() => {
    setCommentsRefetchKey(prev => prev + 1);
  }, []);

  // count comments for product details and manufacturer site
  const productDetailsCommentsCount = productDetailsComments.length;
  const manufacturerSiteCommentsCount = manufacturerSiteComments.length;

  // create custom subforms for product details and manufacturers site
  const customSubForms = React.useMemo(() => {
    const customs = [];
    if (hasProductDetails) {
      customs.push({
        id: 'custom_product_details',
        name: 'Product Details',
        isCustom: true,
        approved: isProductDetailsApproved ? 1 : 0,
        hasComments: productDetailsCommentsCount > 0,
      });
    }
    if (hasManufacturersSite) {
      customs.push({
        id: 'custom_manufacturers_site',
        name: 'Manufacturers Site',
        isCustom: true,
        approved: isManufacturerSiteApproved ? 1 : 0,
        hasComments: manufacturerSiteCommentsCount > 0,
      });
    }
    return customs;
  }, [hasProductDetails, hasManufacturersSite, isProductDetailsApproved, isManufacturerSiteApproved, productDetailsCommentsCount, manufacturerSiteCommentsCount]);

  // merge subForms with custom subforms (for tab list initialization)
  const allSubForms = React.useMemo(() => {
    return [...subForms, ...customSubForms];
  }, [subForms, customSubForms]);

  const [internalActiveTab, setInternalActiveTab] = useActiveTab(allSubForms);
  
  // wrap tab change to sync data before switching
  const wrappedSetInternalActiveTab = React.useCallback(async (newTabId) => {
    // get current form data synchronously BEFORE switching
    if (onFormDataChange && typeof onFormDataChange === 'function' && editable) {
      try {
        // trigger immediate data extraction
        onFormDataChange();
      } catch (error) {
        console.error("[FormSectionsCustomer] error extracting form data:", error);
      }
    }

    // sync data before tab change if syncFormSectionsData is available
    if (syncFormSectionsData && editable) {
      try {
        await syncFormSectionsData();
      } catch (error) {
        console.error("[FormSectionsCustomer] error syncing form data:", error);
      }
    }

    setInternalActiveTab(newTabId);
  }, [syncFormSectionsData, setInternalActiveTab, editable, onFormDataChange]);

  // Use external tab state if provided, otherwise use internal state
  const activeTab = externalActiveTab || internalActiveTab;
  const setActiveTab = externalOnTabChange || wrappedSetInternalActiveTab;

  // use the hook for regular subforms only (if not skipping)
  const {
    subForms: subFormsWithApproval,
  } = useSubFormApproval({
    enduserFilledFormId: skipApprovalFetch ? null : filledForm?.id,
    subForms: skipApprovalFetch ? [] : subForms,
    persona: "customer"
  });

  // merge regular subforms with custom forms and their approval status
  const allFormsWithApproval = React.useMemo(() => {
    const regularForms = skipApprovalFetch ? subForms : subFormsWithApproval;
    return [
      ...regularForms,
      ...customSubForms
    ];
  }, [skipApprovalFetch, subForms, subFormsWithApproval, customSubForms]);

  return (
    <FormSectionsLayout
      forms={allFormsWithApproval}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      prevComments={prevComments}
      requestDetails={requestDetails}
      requestProgressId={requestProgressId}
      persona="customer"
      filledForm={filledForm}
      requestId={requestId}
      handleAddComment={handleAddCommentWrapper}
      enableAddComment={enableAddCommentProp}
      subformActionButtons={null}
      showRequestBadges={showRequestBadges}
      editable={editable}
      onFormDataChange={onFormDataChange}
      skipApprovalFetch={true}
      formsWithApprovalStatus={allFormsWithApproval}
      errors={errors}
      hasProductDetails={hasProductDetails}
      hasManufacturersSite={hasManufacturersSite}
      isSendbackMode={isSendbackMode}
      productDetailsData={productDetailsData}
      customFormApprovalProps={{
        onCommentAdded: handleCommentAdded,
      }}
      formActionButtons={
        !isReadOnly && requestProgressId && (
          <>
            <CommitteeReviewButtons
              requestProgressId={requestProgressId}
              persona="customer"
            />
          </>
        )
      }
    />
  );
};
